import { computed } from 'mobx';
import {
    CreatePartnerRequest,
    PartnerDto,
    UpdatePartnerRequest,
} from 'src/generated-api-client';
import { partnersApi } from 'src/services/apiServices';
import { OptionType } from 'src/stores/ResourceStore/ResourceStore';
import { BasicStore } from 'src/utils/mobx/BasicStore/BasicStore';
import { BasicStoreApi } from 'src/utils/mobx/BasicStore/BasicStore.types';
import { Pager } from 'src/utils/mobx/Pager';
import { Sorter, SorterDirection } from 'src/utils/mobx/Sorter';
import { RequestHelper } from 'src/utils/RequestHelper';

export class PartnersStoreClass extends BasicStore<
    PartnerDto,
    Record<string, any>,
    UpdatePartnerRequest,
    CreatePartnerRequest
> {
    pager = new Pager();
    sorter = new Sorter<PartnerDto>(['createdAt', SorterDirection.DESC]);

    api: BasicStoreApi<
        PartnerDto,
        UpdatePartnerRequest,
        CreatePartnerRequest,
        PartnerDto
    > = {
        loadList: async (...args) => {
            const options = RequestHelper.getOptionsFromArgs(args);

            const response = await RequestHelper.unwrapFromAxiosPromise(
                partnersApi.findAllPartnersForBackoffice(
                    {
                        page: this.pager?.page,
                        size: this.pager?.size,
                        sort: this.sorter?.sort,
                    },
                    options,
                ),
            );
            this.pager?.setTotal(response.totalElements ?? 0);

            return response.content ?? [];
        },
        loadItem: async (id, ...args) => {
            const options = RequestHelper.getOptionsFromArgs(args);

            const response = await RequestHelper.unwrapFromAxiosPromise(
                partnersApi.getPartnerForBackoffice({ id }, options),
            );

            return response ?? null;
        },
        updateItem: async (id, data, options) => {
            const response = await RequestHelper.unwrapFromAxiosPromise(
                partnersApi.updatePartnerForBackoffice(
                    {
                        id: String(id),
                        updatePartnerRequest: data,
                    },
                    options,
                ),
            );

            return response ?? null;
        },
        createItem: async (data, options) => {
            const response = await RequestHelper.unwrapFromAxiosPromise(
                partnersApi.createPartnerForBackoffice(
                    {
                        createPartnerRequest: data,
                    },
                    options,
                ),
            );

            return response ?? null;
        },
    };

    @computed
    get partnerAsOptions() {
        return this.list.map<OptionType<PartnerDto>>((partner) => {
            return {
                label: partner.arabicName,
                value: partner.id,
                item: partner,
            };
        });
    }
}

// Shared singleton instance of Partners store
export const PartnersStore = new PartnersStoreClass();
